import {
    Analytics as AnalyticsIcon,
    Assessment,
    Event as CalendarIcon,
    ShowChart as ChartIcon,
    CompareArrows,
    Extension as CustomIcon,
    Dashboard as DashboardIcon,
    CloudDownload as DataFetcherIcon,
    Storage as DataIcon,
    DonutSmall as DonutIcon,
    DynamicFeed as FeedIcon,
    Folder,
    Gavel,
    AccountTree as GraphIcon,
    Insights as InsightsIcon,
    LocalShipping,
    People,
    AccountBalance as PortfolioIcon,
    StackedLineChart as StackedLineChartIcon,
    TrendingUp as VolatilityIcon,
    VolumeUp
} from '@mui/icons-material';
import React from 'react';
import type { WidgetDefinition, WidgetIOSchema } from '../types/index.js';
import { AkemIntelligenceWidgetDefinition } from '../widgets/AkemIntelligenceWidget.tsx';
import { AkemTickerWidget } from '../widgets/AkemTickerWidget.tsx';
import { CalendarWidget } from '../widgets/CalendarWidget.tsx';
import { CustomerSegmentsWidget } from '../widgets/CustomerSegmentsWidget.tsx';
import { DataFetcherWidget } from '../widgets/DataFetcherWidget.js';
import { FinancialStatementsWidget } from '../widgets/FinancialStatementsWidget.tsx';
import { ImpactAnalysisWidget } from '../widgets/ImpactAnalysisWidget.js';
import { ImpactDashboardWidget } from '../widgets/ImpactDashboardWidget.js';
import { ImpactExplorerWidget } from '../widgets/ImpactExplorerWidget.js';
import { ImpactGraphWidget } from '../widgets/ImpactGraphWidget.js';
import { IndicesOverviewWidget } from '../widgets/IndicesOverviewWidget.tsx';
import { KeyRatiosWidget } from '../widgets/KeyRatiosWidget.tsx';
import { KeyStatsWidget } from '../widgets/KeyStatsWidget.tsx';
import { LegislationWidget } from '../widgets/LegislationWidget.tsx';
import { MarketDataWidget } from '../widgets/MarketDataWidget.js';
import { NewsSentimentWidget } from '../widgets/NewsSentimentWidget.tsx';
import { PortfolioComparisonWidget } from '../widgets/PortfolioComparisonWidget.js';
import { PortfolioOverviewWidget } from '../widgets/PortfolioOverviewWidget.tsx';
import { PriceChartWidget } from '../widgets/PriceChartWidget.tsx';
import { ProfileHeaderWidget } from '../widgets/ProfileHeaderWidget.tsx';
import { RelationshipGraphWidget } from '../widgets/RelationshipGraphWidget.tsx';
import { RiskMetricsWidget } from '../widgets/RiskMetricsWidget.js';
import { RunHeaderWidget } from '../widgets/RunHeaderWidget.tsx';
import { SecondOrderSummaryWidget } from '../widgets/SecondOrderSummaryWidget.tsx';
import { SectorOverviewWidget } from '../widgets/SectorOverviewWidget.tsx';
import { SupplyChainWidget } from '../widgets/SupplyChainWidget.tsx';
import { ThemeAnalysisWidget } from '../widgets/ThemeAnalysisWidget.tsx';
import { VolatilityLiquidityWidget } from '../widgets/VolatilityLiquidityWidget.js';
import { VolumeScannerWidget } from '../widgets/VolumeScannerWidget.js';
import { WidgetGroupWidget } from '../widgets/WidgetGroupWidget.js';

class WidgetRegistryClass {
  private widgets = new Map<string, WidgetDefinition>();

  register(definition: WidgetDefinition): void {
    this.widgets.set(definition.type, definition);
  }

  get(type: string): WidgetDefinition | undefined {
    return this.widgets.get(type);
  }

  list(): WidgetDefinition[] {
    return Array.from(this.widgets.values());
  }

  getByCategory(category: string): WidgetDefinition[] {
    return this.list().filter((widget) => widget.category === category);
  }
}

// Create singleton instance
export const widgetRegistry = new WidgetRegistryClass();

export const highlightOutputSchema: WidgetIOSchema = {
  type: 'object',
  description: 'Shared Highlight contract (packages/shared/src/contracts/schemas.ts)',
  properties: {
    id: { type: 'string' },
    ts: { type: 'number', description: 'Epoch milliseconds' },
    direction: { type: 'number', constraints: { enum: [-1, 0, 1] } },
    magnitude: { type: 'number', constraints: { min: 0, max: 1 } },
    title: { type: 'string' },
    summary: { type: 'string', description: 'Optional' },
    kind: {
      type: 'string',
      constraints: { enum: ['impact', 'news', 'relationship', 'theme'] },
    },
    sourceIds: {
      type: 'array',
      description: 'Optional underlying ids',
      items: { type: 'string' },
    },
    path: {
      type: 'array',
      description: 'Optional nth-order path',
      items: { type: 'string' },
    },
    relatedSymbols: {
      type: 'array',
      description: 'Optional tickers',
      items: { type: 'string', format: 'stock-symbol' },
    },
    link: { type: 'string', description: 'Optional canonical link' },
  },
};

export const calendarEventOutputSchema: WidgetIOSchema = {
  type: 'object',
  description: 'Shared CalendarEvent contract (packages/shared/src/contracts/schemas.ts)',
  properties: {
    id: { type: 'string' },
    ts: { type: 'number', description: 'Epoch milliseconds' },
    type: {
      type: 'string',
      constraints: { enum: ['earnings', 'filing', 'dividend', 'split', 'event'] },
    },
    label: { type: 'string' },
    link: { type: 'string', description: 'Optional resource link' },
    metadata: { type: 'object', description: 'Optional payload' },
  },
};

const IMPACT_SEVERITY_WEIGHTS: Record<string, number> = {
  high: 1,
  medium: 0.6,
  low: 0.3,
};

// Adapt Impact Analysis outputs (Impact objects) into Impact Graph result shape on-the-fly.
const transformImpactAnalysisToGraphResults = (value: any) => {
  if (!Array.isArray(value)) return value;

  return value.map((item) => {
    if (!item || typeof item !== 'object') {
      return item;
    }

    if ('score' in item && 'node' in item) {
      return item;
    }

    if (!('impact' in item)) {
      return item;
    }

    const severity = IMPACT_SEVERITY_WEIGHTS[String((item as any).impact)] ?? 0.5;
    const confidence = typeof (item as any).confidence === 'number' ? (item as any).confidence : 0.5;
    const score = confidence * severity;

    const affectedStocks = Array.isArray((item as any).affectedStocks)
      ? (item as any).affectedStocks.map((stock: unknown) => String(stock))
      : [];

    const node =
      (affectedStocks[0] && String(affectedStocks[0])) ||
      (typeof (item as any).title === 'string' && (item as any).title.trim().length > 0
        ? (item as any).title
        : String((item as any).type ?? 'Impact'));

    const path =
      affectedStocks.length > 0
        ? [typeof (item as any).title === 'string' && (item as any).title.trim().length > 0 ? (item as any).title : node, ...affectedStocks]
        : undefined;

    return {
      node,
      score,
      path,
    };
  });
};

const marketIndexOutputSchema: WidgetIOSchema = {
  type: 'object',
  description: 'Market index snapshot used by indices overview widget',
  properties: {
    id: { type: 'string' },
    name: { type: 'string' },
    symbol: { type: 'string', format: 'stock-symbol' },
    level: { type: 'number' },
    changePercent: { type: 'number' },
    intradaySparkline: {
      type: 'array',
      items: { type: 'number' },
      description: 'Normalized intraday sparkline values',
    },
  },
};

const sectorPerformanceOutputSchema: WidgetIOSchema = {
  type: 'object',
  description: 'Sector performance snapshot with leaders and laggards',
  properties: {
    id: { type: 'string' },
    name: { type: 'string' },
    changePercent: { type: 'number' },
    leaders: { type: 'array', items: { type: 'string', format: 'stock-symbol' } },
    laggards: { type: 'array', items: { type: 'string', format: 'stock-symbol' } },
  },
};

const portfolioOverviewOutputSchema: WidgetIOSchema = {
  type: 'object',
  description: 'Portfolio overview with holdings and performance',
  properties: {
    id: { type: 'string' },
    name: { type: 'string' },
    totalValue: { type: 'number' },
    changePercent: { type: 'number' },
    holdings: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          symbol: { type: 'string', format: 'stock-symbol' },
          weight: { type: 'number' },
          changePercent: { type: 'number' },
        },
      },
    },
  },
};

const secondOrderDomainOutputSchema: WidgetIOSchema = {
  type: 'string',
  description: 'Second-order summary domain identifier',
  constraints: {
    enum: ['customers', 'suppliers', 'marketTrends', 'politics', 'regulatory', 'mna', 'logistics', 'themes'],
  },
};

// Register Data Fetcher Widget (foundational widget)
widgetRegistry.register({
  type: 'dataFetcher',
  name: 'Data Fetcher',
  description: 'Universal data entry point with configurable sources and quality validation',
  category: 'data',
  icon: DataFetcherIcon,
  component: DataFetcherWidget,
  defaultConfig: {
    title: 'Data Fetcher',
    refreshInterval: 0,
  },
  defaultSize: { width: 400, height: 500 },
  inputDefinitions: [],
  outputDefinitions: [
    {
      id: 'data',
      name: 'Fetched Data',
      type: 'any',
      description: 'The fetched data from the selected source',
      schema: {
        type: 'any',
      },
    },
    {
      id: 'metadata',
      name: 'Fetch Metadata',
      type: 'object',
      description: 'Metadata about the fetch operation',
      schema: {
        type: 'object',
        properties: {
          source: { type: 'string' },
          timestamp: { type: 'string', format: 'date-time' },
          parameters: { type: 'object' },
          dataQuality: {
            type: 'object',
            properties: {
              score: { type: 'number', constraints: { min: 0, max: 100 } },
              issues: { type: 'number' },
              warnings: { type: 'number' },
            },
          },
        },
      },
    },
  ],
});

widgetRegistry.register(AkemIntelligenceWidgetDefinition);

// Market overview widgets
widgetRegistry.register({
  type: 'calendar',
  name: 'Market Calendar',
  description: 'Upcoming macro and corporate events with shared CalendarEvent output',
  category: 'market',
  icon: CalendarIcon,
  component: CalendarWidget,
  defaultConfig: { title: 'Market Calendar' },
  defaultSize: { width: 720, height: 280 },
  inputDefinitions: [],
  outputDefinitions: [
    {
      id: 'selectedEvent',
      name: 'Selected Event',
      type: 'object',
      schema: calendarEventOutputSchema,
      description: 'Highlighted calendar event selected by the user',
    },
  ],
});

widgetRegistry.register({
  type: 'akemTicker',
  name: 'Akem Sauce',
  description: 'Scrolling highlight packets sourced from Akem impact graph',
  category: 'market',
  icon: FeedIcon,
  component: AkemTickerWidget,
  defaultConfig: { title: 'Akem Sauce' },
  defaultSize: { width: 1180, height: 200 },
  inputDefinitions: [],
  outputDefinitions: [
    {
      id: 'highlightSelected',
      name: 'Selected Highlight',
      type: 'object',
      schema: highlightOutputSchema,
      description: 'Highlight chosen via ticker interaction',
    },
  ],
});

widgetRegistry.register({
  type: 'indicesOverview',
  name: 'Indices Overview',
  description: 'Snapshot of major indices with sparkline trend',
  category: 'market',
  icon: StackedLineChartIcon,
  component: IndicesOverviewWidget,
  defaultConfig: { title: 'Indices Overview' },
  defaultSize: { width: 560, height: 200 },
  inputDefinitions: [],
  outputDefinitions: [
    {
      id: 'selectedIndex',
      name: 'Selected Index',
      type: 'object',
      schema: marketIndexOutputSchema,
      description: 'Index snapshot currently in focus',
    },
  ],
});

widgetRegistry.register({
  type: 'sectorOverview',
  name: 'Sector Overview',
  description: 'Day performance by sector with leaders/laggards',
  category: 'market',
  icon: DonutIcon,
  component: SectorOverviewWidget,
  defaultConfig: { title: 'Sectors Overview' },
  defaultSize: { width: 600, height: 200 },
  inputDefinitions: [],
  outputDefinitions: [
    {
      id: 'selectedSector',
      name: 'Selected Sector',
      type: 'object',
      schema: sectorPerformanceOutputSchema,
      description: 'Sector focus for downstream analysis',
    },
  ],
});

widgetRegistry.register({
  type: 'portfolioOverview',
  name: 'Portfolio Overview',
  description: 'Reference portfolio mixes with change and holdings weights',
  category: 'market',
  icon: PortfolioIcon,
  component: PortfolioOverviewWidget,
  defaultConfig: { title: 'Sample Portfolios' },
  defaultSize: { width: 560, height: 200 },
  inputDefinitions: [],
  outputDefinitions: [
    {
      id: 'selectedPortfolio',
      name: 'Selected Portfolio',
      type: 'object',
      schema: portfolioOverviewOutputSchema,
      description: 'Portfolio currently highlighted by the user',
    },
  ],
});

widgetRegistry.register({
  type: 'secondOrderSummary',
  name: 'Second-order Summary',
  description: 'Domain summary cards powering drilldowns and navigation',
  category: 'market',
  icon: InsightsIcon,
  component: SecondOrderSummaryWidget,
  defaultConfig: { title: 'Domain Summary', domain: 'customers' },
  defaultSize: { width: 300, height: 360 },
  inputDefinitions: [],
  outputDefinitions: [
    {
      id: 'selectedDomain',
      name: 'Selected Domain',
      type: 'string',
      schema: secondOrderDomainOutputSchema,
      description: 'Domain key represented by this summary card',
    },
  ],
});

// Register Run Header (lives inside a Run group)
widgetRegistry.register({
  type: 'runHeader',
  name: 'Run Header',
  description: 'Run status, parameters, steps, and cost',
  category: 'custom',
  icon: Folder, // Reuse folder icon for now
  component: RunHeaderWidget as unknown as React.ComponentType<any>,
  defaultConfig: { title: 'Run' },
  defaultSize: { width: 520, height: 240 },
  inputDefinitions: [
    { id: 'status', name: 'Status', type: 'string', schema: { type: 'string', constraints: { enum: ['queued','running','complete','error'] } } },
    { id: 'prompt', name: 'Prompt', type: 'string', schema: { type: 'string' } },
    { id: 'params', name: 'Parameters', type: 'object', schema: { type: 'object' } },
    { id: 'summary', name: 'Summary', type: 'string', schema: { type: 'string' } },
    { id: 'final', name: 'Final Output', type: 'string', schema: { type: 'string' } },
    { id: 'finalView', name: 'Final View', type: 'string', schema: { type: 'string' } },
    { id: 'finalData', name: 'Final Data', type: 'object', schema: { type: 'object' } },
    { id: 'trace', name: 'Trace', type: 'array', schema: { type: 'array', items: { type: 'object' } } },
    { id: 'actionsTaken', name: 'Actions Taken', type: 'array', schema: { type: 'array', items: { type: 'string' } } },
    { id: 'nextSteps', name: 'Next Steps', type: 'array', schema: { type: 'array', items: { type: 'string' } } },
    { id: 'cost', name: 'Cost', type: 'object', schema: { type: 'object', properties: { estimatedUsd: { type: 'number' } } } },
  ],
  outputDefinitions: [],
});

// Register Profile Widgets (skeleton-ready)
widgetRegistry.register({
  type: 'profileHeader',
  name: 'Profile Header',
  description: 'Company identity and key KPIs',
  category: 'analysis',
  icon: DashboardIcon,
  component: ProfileHeaderWidget as unknown as React.ComponentType<any>,
  defaultConfig: { title: 'Profile Header', showLogo: true },
  defaultSize: { width: 800, height: 150 },
  inputDefinitions: [
    { id: 'profile', name: 'Profile', type: 'object', description: 'Profile data', schema: { type: 'object' } },
  ],
  outputDefinitions: [
    { id: 'ticker', name: 'Ticker', type: 'string', description: 'Ticker symbol', schema: { type: 'string' } },
  ],
});

widgetRegistry.register({
  type: 'customerSegments',
  name: 'Customer Segments',
  description: 'Key customer segments, trends, and risks',
  category: 'analysis',
  icon: People,
  component: CustomerSegmentsWidget as unknown as React.ComponentType<any>,
  defaultConfig: { title: 'Customer Segments' },
  defaultSize: { width: 400, height: 400 },
  inputDefinitions: [
    { id: 'profile', name: 'Profile', type: 'object', description: 'Profile data', schema: { type: 'object' } },
  ],
  outputDefinitions: [],
});

widgetRegistry.register({
  type: 'legislation',
  name: 'Legislation',
  description: 'Relevant legislation and regulatory bills',
  category: 'analysis',
  icon: Gavel,
  component: LegislationWidget as unknown as React.ComponentType<any>,
  defaultConfig: { title: 'Legislation' },
  defaultSize: { width: 400, height: 400 },
  inputDefinitions: [
    { id: 'profile', name: 'Profile', type: 'object', description: 'Profile data', schema: { type: 'object' } },
  ],
  outputDefinitions: [],
});

widgetRegistry.register({
  type: 'supplyChain',
  name: 'Supply Chain',
  description: 'Supply chain analysis, risks, and logistics',
  category: 'analysis',
  icon: LocalShipping,
  component: SupplyChainWidget as unknown as React.ComponentType<any>,
  defaultConfig: { title: 'Supply Chain' },
  defaultSize: { width: 400, height: 400 },
  inputDefinitions: [
    { id: 'profile', name: 'Profile', type: 'object', description: 'Profile data', schema: { type: 'object' } },
  ],
  outputDefinitions: [],
});

widgetRegistry.register({
  type: 'relationshipGraph',
  name: 'Relationship Graph',
  description: 'Suppliers, customers, competitors',
  category: 'analysis',
  icon: GraphIcon,
  component: RelationshipGraphWidget as unknown as React.ComponentType<any>,
  defaultConfig: { title: 'Relationships' },
  defaultSize: { width: 430, height: 400 },
  inputDefinitions: [
    { id: 'relationships', name: 'Relationships', type: 'object', description: 'relationships slice', schema: { type: 'object' } },
    { id: 'focusEntity', name: 'Focus Entity', type: 'object', description: 'Selected entity to focus', schema: { type: 'object' }, required: false },
  ],
  outputDefinitions: [
    { id: 'selectedEntity', name: 'Selected Entity', type: 'object', description: 'Entity selected by user', schema: { type: 'object' } },
    { id: 'drilldownRequest', name: 'Drilldown Request', type: 'object', description: 'Request to drilldown', schema: { type: 'object' } },
  ],
});

widgetRegistry.register({
  type: 'themeAnalysis',
  name: 'Theme Analysis',
  description: 'Top themes with sentiment and evidence',
  category: 'analysis',
  icon: AnalyticsIcon,
  component: ThemeAnalysisWidget as unknown as React.ComponentType<any>,
  defaultConfig: { title: 'Themes' },
  defaultSize: { width: 400, height: 300 },
  inputDefinitions: [
    {
      id: 'filterStock',
      name: 'Filter by Stock',
      type: 'object',
      description: 'Stock selection to analyze themes for',
      required: false,
      schema: {
        type: 'object',
        properties: {
          symbol: { type: 'string', format: 'stock-symbol' },
        },
      },
    },
    { id: 'themes', name: 'Themes', type: 'array', description: 'Theme list', schema: { type: 'array', items: { type: 'object' } } },
  ],
  outputDefinitions: [],
});

widgetRegistry.register({
  type: 'newsSentiment',
  name: 'News & Sentiment',
  description: 'Recent headlines with scores',
  category: 'analysis',
  icon: VolumeUp,
  component: NewsSentimentWidget as unknown as React.ComponentType<any>,
  defaultConfig: { title: 'News & Sentiment' },
  defaultSize: { width: 380, height: 300 },
  inputDefinitions: [
    { id: 'recentNews', name: 'Recent News', type: 'array', description: 'News items', schema: { type: 'array', items: { type: 'object' } } },
  ],
  outputDefinitions: [],
});

// Register built-in widgets
widgetRegistry.register({
  type: 'marketData',
  name: 'Market Data',
  description: 'Real-time stock quotes and market data',
  category: 'market',
  icon: ChartIcon,
  component: MarketDataWidget,
  defaultConfig: { 
    title: 'Market Data',
    symbols: ['AAPL', 'GOOGL', 'MSFT', 'AMZN', 'TSLA'],
  },
  defaultSize: { width: 350, height: 400 },
  inputDefinitions: [],
  outputDefinitions: [
    { 
      id: 'selectedStock', 
      name: 'Selected Stock', 
      type: 'object',
      description: 'The currently selected stock data',
      schema: {
        type: 'object',
        properties: {
          symbol: { type: 'string', format: 'stock-symbol' },
          name: { type: 'string' },
          price: { type: 'number' },
          change: { type: 'number' },
          changePercent: { type: 'number', format: 'percentage' },
          volume: { type: 'number' },
          marketCap: { type: 'number' },
        },
      },
    },
    {
      id: 'stockData',
      name: 'All Stock Data',
      type: 'array',
      description: 'All stock data in the widget',
      schema: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            symbol: { type: 'string', format: 'stock-symbol' },
            name: { type: 'string' },
            price: { type: 'number' },
            change: { type: 'number' },
            changePercent: { type: 'number', format: 'percentage' },
            volume: { type: 'number' },
            marketCap: { type: 'number' },
          },
        },
      },
    },
  ],
});

// Price Chart widget (mock-backed for prototype)
widgetRegistry.register({
  type: 'priceChart',
  name: 'Price Chart',
  description: 'Candlestick chart with ranges',
  category: 'market',
  icon: ChartIcon,
  component: PriceChartWidget as unknown as React.ComponentType<any>,
  defaultConfig: { title: 'Price', ticker: 'AAPL' },
  defaultSize: { width: 500, height: 320 },
  inputDefinitions: [
    { id: 'ticker', name: 'Ticker', type: 'string', description: 'Ticker symbol', schema: { type: 'string' }, required: false },
  ],
  outputDefinitions: [],
});

widgetRegistry.register({
  type: 'keyStats',
  name: 'Key Stats',
  description: 'Snapshot metrics',
  category: 'market',
  icon: Assessment,
  component: KeyStatsWidget as unknown as React.ComponentType<any>,
  defaultConfig: { title: 'Key Stats', ticker: 'AAPL' },
  defaultSize: { width: 300, height: 230 },
  inputDefinitions: [
    { id: 'ticker', name: 'Ticker', type: 'string', schema: { type: 'string' }, required: false },
  ],
  outputDefinitions: [],
});


widgetRegistry.register({
  type: 'financialStatements',
  name: 'Financial Statements',
  description: 'Comprehensive financial statements with Income, Balance Sheet, and Cash Flow',
  category: 'analysis',
  icon: Assessment,
  component: FinancialStatementsWidget as unknown as React.ComponentType<any>,
  defaultConfig: { title: 'Financial Statements' },
  defaultSize: { width: 800, height: 600 },
  inputDefinitions: [],
  outputDefinitions: [],
});


// Key Ratios widget (scaffold; backed by /profile/{id}/ratios)
widgetRegistry.register({
  type: 'keyRatios',
  name: 'Key Ratios',
  description: 'TTM ratios and margins (stubbed API)',
  category: 'analysis',
  icon: AnalyticsIcon,
  component: KeyRatiosWidget as unknown as React.ComponentType<any>,
  defaultConfig: { title: 'Key Ratios' },
  defaultSize: { width: 400, height: 240 },
  inputDefinitions: [
    { id: 'ticker', name: 'Ticker', type: 'string', schema: { type: 'string' }, required: false },
  ],
  outputDefinitions: [],
});

// Impact Analysis widget with enhanced schema
widgetRegistry.register({
  type: 'impactAnalysis',
  name: 'Impact Analysis',
  description: 'Market impact analysis and insights',
  category: 'analysis',
  icon: AnalyticsIcon,
  component: ImpactAnalysisWidget,
  defaultConfig: { title: 'Impact Analysis' },
  defaultSize: { width: 450, height: 500 },
  inputDefinitions: [
    { 
      id: 'filterStock', 
      name: 'Filter by Stock', 
      type: 'object',
      description: 'Stock to filter impacts by',
      required: false,
      schema: {
        type: 'object',
        properties: {
          symbol: { type: 'string', format: 'stock-symbol' },
        },
      },
      transformer: (stockData: any) => stockData, // Pass the full stock object
    },
  ],
  outputDefinitions: [
    { 
      id: 'selectedImpact', 
      name: 'Selected Impact', 
      type: 'object',
      description: 'The currently selected impact',
      schema: {
        type: 'object',
        properties: {
          type: { type: 'string', constraints: { enum: ['market', 'sector', 'stock'] } },
          title: { type: 'string' },
          description: { type: 'string' },
          confidence: { type: 'number', constraints: { min: 0, max: 1 } },
          impact: { type: 'string', constraints: { enum: ['high', 'medium', 'low'] } },
          affectedStocks: { type: 'array', items: { type: 'string', format: 'stock-symbol' } },
        },
      },
    },
    {
      id: 'filteredImpacts',
      name: 'Filtered Impacts',
      type: 'array',
      description: 'All impacts (filtered if stock input provided)',
      schema: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            type: { type: 'string' },
            title: { type: 'string' },
            impact: { type: 'string' },
          },
        },
      },
    },
    {
      id: 'marketSentiment',
      name: 'Market Sentiment',
      type: 'string',
      description: 'Overall market sentiment',
      schema: {
        type: 'string',
        constraints: { enum: ['bullish', 'bearish', 'neutral'] },
      },
    },
    {
      id: 'analyzedStock',
      name: 'Analyzed Stock',
      type: 'object',
      description: 'Stock data with AI analysis',
      schema: {
        type: 'object',
        properties: {
          symbol: { type: 'string', format: 'stock-symbol' },
          price: { type: 'number' },
          volume: { type: 'number' },
          sentiment: { type: 'string' },
          summary: { type: 'string' },
          keyPoints: { type: 'array', items: { type: 'string' } },
        },
      },
    },
  ],
});

widgetRegistry.register({
  type: 'impactExplorer',
  name: 'Impact Explorer',
  description: 'Interactive mini app for exploring market, sector, and stock impacts',
  category: 'analysis',
  icon: AnalyticsIcon,
  component: ImpactExplorerWidget,
  defaultConfig: { title: 'Impact Explorer' },
  // Smaller footprint for condensed view
  // 2x2 tile grid in condensed view
  defaultSize: { width: 380, height: 160 },
  inputDefinitions: [
    {
      id: 'filterStock',
      name: 'Filter by Stock',
      type: 'object',
      description: 'Stock to filter impacts by',
      required: false,
      schema: {
        type: 'object',
        properties: {
          symbol: { type: 'string', format: 'stock-symbol' },
        },
      },
      transformer: (stockData: any) => stockData,
    },
  ],
  outputDefinitions: [
    {
      id: 'selectedImpact',
      name: 'Selected Impact',
      type: 'object',
      description: 'The currently selected impact',
      schema: {
        type: 'object',
        properties: {
          type: { type: 'string', constraints: { enum: ['market', 'sector', 'stock'] } },
          title: { type: 'string' },
          description: { type: 'string' },
          confidence: { type: 'number', constraints: { min: 0, max: 1 } },
          impact: { type: 'string', constraints: { enum: ['high', 'medium', 'low'] } },
          affectedStocks: { type: 'array', items: { type: 'string', format: 'stock-symbol' } },
        },
      },
    },
    {
      id: 'filteredImpacts',
      name: 'Filtered Impacts',
      type: 'array',
      description: 'All impacts (filtered if stock input provided)',
      schema: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            type: { type: 'string' },
            title: { type: 'string' },
            impact: { type: 'string' },
          },
        },
      },
    },
    {
      id: 'marketSentiment',
      name: 'Market Sentiment',
      type: 'string',
      description: 'Overall market sentiment',
      schema: {
        type: 'string',
        constraints: { enum: ['bullish', 'bearish', 'neutral'] },
      },
    },
    {
      id: 'analyzedStock',
      name: 'Analyzed Stock',
      type: 'object',
      description: 'Stock data with AI analysis',
      schema: {
        type: 'object',
        properties: {
          symbol: { type: 'string', format: 'stock-symbol' },
          price: { type: 'number' },
          volume: { type: 'number' },
          sentiment: { type: 'string' },
          summary: { type: 'string' },
          keyPoints: { type: 'array', items: { type: 'string' } },
        },
      },
    },
    {
      id: 'summary',
      name: 'Summary',
      type: 'string',
      description: 'High level summary of impacts',
      schema: { type: 'string' },
    },
  ],
});

// Impact Dashboard widget - consumes Impact Analysis outputs
widgetRegistry.register({
  type: 'impactDashboard',
  name: 'Impact Dashboard',
  description: 'Visualize and analyze impact data from Impact Analysis widget',
  category: 'analysis',
  icon: DashboardIcon,
  component: ImpactDashboardWidget,
  defaultConfig: { title: 'Impact Dashboard' },
  defaultSize: { width: 500, height: 550 },
  inputDefinitions: [
    {
      id: 'selectedImpact',
      name: 'Selected Impact',
      type: 'object',
      description: 'The selected impact to display in detail',
      required: false,
      schema: {
        type: 'object',
        properties: {
          type: { type: 'string' },
          title: { type: 'string' },
          description: { type: 'string' },
          impact: { type: 'string' },
        },
      },
    },
    {
      id: 'filteredImpacts',
      name: 'Filtered Impacts',
      type: 'array',
      description: 'All filtered impacts for overview metrics',
      required: false,
      schema: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            type: { type: 'string' },
            impact: { type: 'string' },
          },
        },
      },
    },
    {
      id: 'marketSentiment',
      name: 'Market Sentiment',
      type: 'string',
      description: 'Overall market sentiment',
      required: false,
      schema: {
        type: 'string',
        constraints: { enum: ['bullish', 'bearish', 'neutral'] },
      },
    },
    {
      id: 'analyzedStock',
      name: 'Analyzed Stock',
      type: 'object',
      description: 'Stock data with AI analysis',
      required: false,
      schema: {
        type: 'object',
        properties: {
          symbol: { type: 'string', format: 'stock-symbol' },
          price: { type: 'number' },
          volume: { type: 'number' },
          sentiment: { type: 'string' },
          summary: { type: 'string' },
          keyPoints: { type: 'array', items: { type: 'string' } },
        },
      },
    },
  ],
  outputDefinitions: [], // This widget is a display widget, no outputs
  connectionGroups: [
    {
      sourceWidgetType: 'impactAnalysis',
      description: 'Connect all Impact Analysis data',
      connections: [
        { from: 'selectedImpact', to: 'selectedImpact' },
        { from: 'filteredImpacts', to: 'filteredImpacts' },
        { from: 'marketSentiment', to: 'marketSentiment' },
        { from: 'analyzedStock', to: 'analyzedStock' },
      ],
    },
  ],
});

// Impact Graph widget - displays nth-order impact results and paths
widgetRegistry.register({
  type: 'impactGraph',
  name: 'Impact Graph',
  description: 'Visualize nth-order impact results and path explanations',
  category: 'analysis',
  icon: GraphIcon,
  component: ImpactGraphWidget,
  defaultConfig: { title: 'Impact Graph' },
  defaultSize: { width: 700, height: 420 },
  inputDefinitions: [
    {
      id: 'impactResults',
      name: 'Impact Results',
      type: 'array',
      description: 'Results from impact propagation (node, score, path)',
      schema: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            node: { type: 'string' },
            score: { type: 'number' },
            path: { type: 'array', items: { type: 'string' } },
          },
        },
      },
      transformer: transformImpactAnalysisToGraphResults,
    },
    {
      id: 'parameters',
      name: 'Propagation Parameters',
      type: 'object',
      description: 'Depth, decay, horizonDays for display context',
      schema: {
        type: 'object',
        properties: {
          depth: { type: 'number' },
          decay: { type: 'number' },
          horizonDays: { type: 'number' },
        },
      },
    },
  ],
  outputDefinitions: [
    {
      id: 'selectedTarget',
      name: 'Selected Target',
      type: 'object',
      description: 'The selected impacted node with score and path',
      schema: {
        type: 'object',
        properties: {
          node: { type: 'string' },
          score: { type: 'number' },
          path: { type: 'array', items: { type: 'string' } },
        },
      },
    },
  ],
});

// Portfolio Comparison widget
widgetRegistry.register({
  type: 'portfolioComparison',
  name: 'Portfolio Comparison',
  description: 'Compare multiple stocks side-by-side with key metrics',
  category: 'analysis',
  icon: CompareArrows,
  component: PortfolioComparisonWidget,
  defaultConfig: { title: 'Portfolio Comparison' },
  defaultSize: { width: 600, height: 650 },
  inputDefinitions: [
    {
      id: 'stock1',
      name: 'Stock 1',
      type: 'object',
      description: 'First stock to compare',
      required: true,
      schema: {
        type: 'object',
        properties: {
          symbol: { type: 'string', format: 'stock-symbol' },
          price: { type: 'number' },
          volume: { type: 'number' },
          sentiment: { type: 'string' },
        },
      },
    },
    {
      id: 'stock2',
      name: 'Stock 2',
      type: 'object',
      description: 'Second stock to compare',
      required: true,
      schema: {
        type: 'object',
        properties: {
          symbol: { type: 'string', format: 'stock-symbol' },
          price: { type: 'number' },
          volume: { type: 'number' },
          sentiment: { type: 'string' },
        },
      },
    },
    {
      id: 'stock3',
      name: 'Stock 3 (Optional)',
      type: 'object',
      description: 'Third stock to compare',
      required: false,
      schema: {
        type: 'object',
        properties: {
          symbol: { type: 'string', format: 'stock-symbol' },
          price: { type: 'number' },
          volume: { type: 'number' },
          sentiment: { type: 'string' },
        },
      },
    },
  ],
  outputDefinitions: [
    {
      id: 'comparisonData',
      name: 'Comparison Data',
      type: 'array',
      description: 'Detailed comparison metrics',
      schema: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            name: { type: 'string' },
            stock1Value: { type: 'any' },
            stock2Value: { type: 'any' },
            comparison: { type: 'string' },
          },
        },
      },
    },
    {
      id: 'portfolioScore',
      name: 'Portfolio Score',
      type: 'object',
      description: 'Overall portfolio analysis scores',
      schema: {
        type: 'object',
        properties: {
          diversification: { type: 'number' },
          sentiment: { type: 'number' },
          overall: { type: 'number' },
        },
      },
    },
    {
      id: 'stocks',
      name: 'Stock List',
      type: 'array',
      description: 'List of analyzed stocks',
      schema: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            symbol: { type: 'string' },
            price: { type: 'number' },
          },
        },
      },
    },
    {
      id: 'portfolioData',
      name: 'Portfolio Data',
      type: 'object',
      description: 'Complete portfolio data for risk analysis',
      schema: {
        type: 'object',
        properties: {
          stocks: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                symbol: { type: 'string' },
                price: { type: 'number' },
                volume: { type: 'number' },
              },
            },
          },
          portfolioScore: {
            type: 'object',
            properties: {
              diversification: { type: 'number' },
              sentiment: { type: 'number' },
              overall: { type: 'number' },
            },
          },
        },
      },
    },
  ],
});

// Risk Metrics widget
widgetRegistry.register({
  type: 'riskMetrics',
  name: 'Risk Metrics',
  description: 'Calculate portfolio risk metrics including volatility, beta, and Sharpe ratio',
  category: 'analysis',
  icon: Assessment,
  component: RiskMetricsWidget,
  defaultConfig: { title: 'Risk Metrics' },
  defaultSize: { width: 550, height: 750 },
  inputDefinitions: [
    {
      id: 'portfolioData',
      name: 'Portfolio Data',
      type: 'object',
      description: 'Portfolio data with stock list',
      required: true,
      schema: {
        type: 'object',
        properties: {
          stocks: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                symbol: { type: 'string' },
                price: { type: 'number' },
                volume: { type: 'number' },
              },
            },
          },
          portfolioScore: {
            type: 'object',
            properties: {
              diversification: { type: 'number' },
              sentiment: { type: 'number' },
              overall: { type: 'number' },
            },
          },
        },
      },
    },
    {
      id: 'marketSentiment',
      name: 'Market Sentiment',
      type: 'string',
      description: 'Overall market sentiment',
      required: false,
      schema: {
        type: 'string',
        constraints: { enum: ['bullish', 'bearish', 'neutral'] },
      },
    },
  ],
  outputDefinitions: [
    {
      id: 'riskMetrics',
      name: 'Risk Metrics',
      type: 'object',
      description: 'Calculated risk metrics',
      schema: {
        type: 'object',
        properties: {
          volatility: { type: 'number' },
          beta: { type: 'number' },
          sharpeRatio: { type: 'number' },
          var95: { type: 'number' },
          overallRisk: { type: 'string' },
        },
      },
    },
    {
      id: 'riskScore',
      name: 'Risk Score',
      type: 'object',
      description: 'Risk scores by category',
      schema: {
        type: 'object',
        properties: {
          overall: { type: 'number' },
          volatility: { type: 'number' },
          beta: { type: 'number' },
          sharpe: { type: 'number' },
          concentration: { type: 'number' },
        },
      },
    },
    {
      id: 'recommendation',
      name: 'Risk Recommendation',
      type: 'string',
      description: 'Risk level recommendation',
      schema: {
        type: 'string',
        constraints: { enum: ['low-risk', 'moderate-risk', 'high-risk'] },
      },
    },
  ],
});

// Widget Group - special widget for grouped components
// Note: This is a special widget type that dynamically inherits
// input/output definitions from the widgets it contains
widgetRegistry.register({
  type: 'widgetGroup',
  name: 'Widget Group',
  description: 'A group of widgets abstracted into a single component',
  category: 'custom',
  icon: Folder,
  component: WidgetGroupWidget,
  defaultConfig: { title: 'Widget Group' },
  defaultSize: { width: 400, height: 300 },
  inputDefinitions: [], // Dynamic based on exposed inputs
  outputDefinitions: [], // Dynamic based on exposed outputs
});

// Volume Scanner Widget
widgetRegistry.register({
  type: 'volumeScanner',
  name: 'Volume Scanner',
  description: 'Detects unusual volume patterns and potential breakouts',
  category: 'analysis',
  icon: VolumeUp,
  component: VolumeScannerWidget,
  defaultConfig: { 
    title: 'Volume Scanner',
    volumeMultiplier: 2,
    timeframe: '15min',
    minDollarVolume: 1000000,
    includeBlockTrades: false,
    alertOnSpikes: true,
    autoRefresh: false,
    refreshInterval: 300
  },
  defaultSize: { width: 700, height: 500 },
  inputDefinitions: [],
  outputDefinitions: [
    {
      id: 'selectedAlert',
      name: 'Selected Alert',
      type: 'object',
      description: 'The currently selected volume alert',
      schema: {
        type: 'object',
        properties: {
          symbol: { type: 'string', format: 'stock-symbol' },
          companyName: { type: 'string' },
          currentVolume: { type: 'number' },
          avgVolume: { type: 'number' },
          volumeRatio: { type: 'number' },
          price: { type: 'number' },
          priceChange: { type: 'number' },
          priceChangePercent: { type: 'number', format: 'percentage' },
          dollarVolume: { type: 'number' },
          trend: { type: 'string', constraints: { enum: ['up', 'down', 'neutral'] } },
          significance: { type: 'string', constraints: { enum: ['high', 'medium', 'low'] } },
        },
      },
    },
    {
      id: 'volumeAlerts',
      name: 'Volume Alerts',
      type: 'array',
      description: 'All current volume alerts',
      schema: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            symbol: { type: 'string', format: 'stock-symbol' },
            companyName: { type: 'string' },
            volumeRatio: { type: 'number' },
            price: { type: 'number' },
            priceChangePercent: { type: 'number', format: 'percentage' },
            significance: { type: 'string' },
          },
        },
      },
    },
    {
      id: 'scanResults',
      name: 'Scan Results',
      type: 'object',
      description: 'Volume scan metadata and summary',
      schema: {
        type: 'object',
        properties: {
          lastScan: { type: 'string' },
          totalScanned: { type: 'number' },
          alertsFound: { type: 'number' },
        },
      },
    },
  ],
});

// Volatility & Liquidity Filter Widget
widgetRegistry.register({
  type: 'volatilityLiquidity',
  name: 'Volatility & Liquidity Filter',
  description: 'Filter stocks by volatility (ATR) and liquidity metrics',
  category: 'analysis',
  icon: VolatilityIcon,
  component: VolatilityLiquidityWidget,
  defaultConfig: {
    title: 'Volatility & Liquidity Filter',
    atrMin: 0,
    atrMax: 10,
    atrPercentMin: 0,
    atrPercentMax: 10,
    avgVolumeMin: 100000,
    bidAskSpreadMax: 5,
    period: 14,
    limit: 50,
  },
  defaultSize: { width: 800, height: 600 },
  inputDefinitions: [],
  outputDefinitions: [
    {
      id: 'stocks',
      name: 'Filtered Stocks',
      type: 'array',
      description: 'Stocks that meet volatility and liquidity criteria',
      schema: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            symbol: { type: 'string', format: 'stock-symbol' },
            price: { type: 'number' },
            atr: { type: 'number' },
            atrPercent: { type: 'number', format: 'percentage' },
            volatilityRank: { type: 'number', constraints: { min: 0, max: 100 } },
            avgVolume30Day: { type: 'number' },
            avgDollarVolume: { type: 'number' },
            bidAskSpread: { type: 'number' },
            bidAskSpreadPercent: { type: 'number', format: 'percentage' },
            liquidityScore: { type: 'number', constraints: { min: 0, max: 100 } },
            recentVolatility: {
              type: 'object',
              properties: {
                daily: { type: 'number', format: 'percentage' },
                weekly: { type: 'number', format: 'percentage' },
                monthly: { type: 'number', format: 'percentage' },
              },
            },
          },
        },
      },
    },
    {
      id: 'marketStats',
      name: 'Market Statistics',
      type: 'object',
      description: 'Overall market statistics from the scan',
      schema: {
        type: 'object',
        properties: {
          avgATR: { type: 'number' },
          avgATRPercent: { type: 'number', format: 'percentage' },
          avgVolume: { type: 'number' },
          totalScanned: { type: 'number' },
          totalFiltered: { type: 'number' },
        },
      },
    },
    {
      id: 'timestamp',
      name: 'Scan Timestamp',
      type: 'string',
      description: 'When the scan was performed',
      schema: {
        type: 'string',
        format: 'date-time',
      },
    },
  ],
});

// Future widget placeholders
const futureWidgets: WidgetDefinition[] = [
  {
    type: 'portfolio',
    name: 'Portfolio Performance',
    description: 'Track portfolio performance and analytics',
    category: 'portfolio',
    icon: PortfolioIcon,
    component: (() => React.createElement('div', null, 'Portfolio Widget (Coming Soon)')) as any,
    defaultConfig: { title: 'Portfolio Performance' },
    defaultSize: { width: 450, height: 350 },
    inputDefinitions: [],
    outputDefinitions: [],
  },
  {
    type: 'dataSource',
    name: 'Data Source',
    description: 'Connect to external data sources',
    category: 'data',
    icon: DataIcon,
    component: (() => React.createElement('div', null, 'Data Source Widget (Coming Soon)')) as any,
    defaultConfig: { title: 'Data Source' },
    defaultSize: { width: 300, height: 200 },
    inputDefinitions: [],
    outputDefinitions: [
      { id: 'data', name: 'Data Output', type: 'any' },
    ],
  },
  {
    type: 'custom',
    name: 'Custom Widget',
    description: 'Create your own custom widget',
    category: 'custom',
    icon: CustomIcon,
    component: (() => React.createElement('div', null, 'Custom Widget (Coming Soon)')) as any,
    defaultConfig: { title: 'Custom Widget' },
    defaultSize: { width: 300, height: 300 },
    inputDefinitions: [],
    outputDefinitions: [],
  },
];

// Register future widgets
futureWidgets.forEach((widget) => widgetRegistry.register(widget));
